import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { fmtCurrency } from '../lib/localeUtils';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Minus, Loader2, AlertTriangle, 
  Package, ShoppingCart, Sparkles, BarChart3, Calendar,
  AlertCircle, CheckCircle, Clock, FileText, Truck
} from 'lucide-react';
import { ForecastConfigCard } from './forecast/ForecastConfigCard';
import { ForecastPODialog } from './forecast/ForecastPODialog';
import { keyed } from '../lib/listKeys';

const TrendIcon = ({ trend }) => {
  if (trend === 'increasing') return <TrendingUp className="h-4 w-4 text-green-500" />;
  if (trend === 'decreasing') return <TrendingDown className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

const StockoutBadge = ({ days }) => {
  if (days <= 7) return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> {days}d</Badge>;
  if (days <= 14) return <Badge variant="secondary" className="bg-amber-500/20 text-amber-700 gap-1"><Clock className="h-3 w-3" /> {days}d</Badge>;
  if (days <= 30) return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> {days}d</Badge>;
  return <Badge variant="outline" className="text-green-600 gap-1"><CheckCircle className="h-3 w-3" /> {days}d+</Badge>;
};

export default function DemandForecastPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [creatingPO, setCreatingPO] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [historyDays, setHistoryDays] = useState(90);
  const [forecastDays, setForecastDays] = useState(30);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [poDialogOpen, setPODialogOpen] = useState(false);
  const [reorderDays, setReorderDays] = useState(30);

  const runForecast = useCallback(async () => {
    setLoading(true);
    setSelectedProducts(new Set());
    try {
      const res = await api.post('/ai/demand-forecast', {
        history_days: historyDays,
        forecast_days: forecastDays
      });
      setForecast(res.data);
      toast.success('Demand forecast generated!', {
        description: `Analyzed ${res.data.products_analyzed} products`
      });
    } catch (err) {
      const { handleAIError } = await import('../lib/aiErrorHandler');
      handleAIError(err, 'Failed to generate forecast');
    } finally {
      setLoading(false);
    }
  }, [historyDays, forecastDays]);

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const selectAllCritical = () => {
    const criticalIds = forecast?.critical_products?.map(p => p.product_id) || [];
    setSelectedProducts(new Set(criticalIds));
  };

  const selectAllWarning = () => {
    const warningIds = forecast?.warning_products?.map(p => p.product_id) || [];
    setSelectedProducts(prev => {
      const next = new Set(prev);
      warningIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleCreatePO = async () => {
    if (selectedProducts.size === 0) {
      toast.error('Please select products to reorder');
      return;
    }

    setCreatingPO(true);
    try {
      const res = await api.post('/ai/auto-generate-po', {
        product_ids: Array.from(selectedProducts),
        reorder_quantity_days: reorderDays
      });
      
      toast.success(`Created ${res.data.purchase_orders?.length || 0} Purchase Order(s)`, {
        description: `Total value: ${fmtCurrency(res.data.total_value || 0)}`
      });
      
      setPODialogOpen(false);
      setSelectedProducts(new Set());
      
      // Navigate to purchases page
      if (res.data.purchase_orders?.length > 0) {
        navigate('/purchases');
      }
    } catch (err) {
      const { handleAIError } = await import('../lib/aiErrorHandler');
      handleAIError(err, 'Failed to create purchase orders');
    } finally {
      setCreatingPO(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="demand-forecast-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <TrendingUp className="h-7 w-7 text-primary" />
            AI Demand Forecast
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Predict future inventory needs using AI-powered sales analysis
          </p>
        </div>
      </div>

      {/* Configuration Card */}
      <ForecastConfigCard
        historyDays={historyDays}
        setHistoryDays={setHistoryDays}
        forecastDays={forecastDays}
        setForecastDays={setForecastDays}
        onRun={runForecast}
        loading={loading}
      />

      {/* Results Section */}
      {forecast && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="summary-products">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
                  <Package className="h-3.5 w-3.5" /> Products Analyzed
                </div>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {forecast.products_analyzed}
                </p>
              </CardContent>
            </Card>
            <Card className="border-red-200 dark:border-red-900" data-testid="summary-critical">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-red-600 text-xs font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" /> Critical Risk
                </div>
                <p className="text-2xl font-bold mt-1 text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {forecast.summary?.critical_stockout_risk || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">≤7 days stock</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 dark:border-amber-900" data-testid="summary-warning">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-amber-600 text-xs font-medium">
                  <Clock className="h-3.5 w-3.5" /> Warning Risk
                </div>
                <p className="text-2xl font-bold mt-1 text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {forecast.summary?.warning_stockout_risk || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">8-14 days stock</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 dark:border-green-900" data-testid="summary-healthy">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-600 text-xs font-medium">
                  <CheckCircle className="h-3.5 w-3.5" /> Healthy Stock
                </div>
                <p className="text-2xl font-bold mt-1 text-green-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {forecast.summary?.healthy_stock || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">&gt;14 days stock</p>
              </CardContent>
            </Card>
          </div>

          {/* AI Insights */}
          {forecast.ai_insights && (
            <Card data-testid="ai-insights-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" /> AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Risk Assessment */}
                {forecast.ai_insights.risk_assessment && (
                  <Alert>
                    <BarChart3 className="h-4 w-4" />
                    <AlertTitle>Risk Assessment</AlertTitle>
                    <AlertDescription>{forecast.ai_insights.risk_assessment}</AlertDescription>
                  </Alert>
                )}
                
                {/* Key Insights */}
                {forecast.ai_insights.insights?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Key Insights</h4>
                    <ul className="space-y-1.5">
                      {keyed(forecast.ai_insights.insights).map(({ item: insight, key }) => (
                        <li key={key} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-primary">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {forecast.ai_insights.recommendations?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Recommendations</h4>
                    <div className="grid gap-2">
                      {keyed(forecast.ai_insights.recommendations).map(({ item: rec, key }) => (
                        <div key={key} className="flex items-start gap-3 p-2 rounded-lg bg-muted/50">
                          <Badge variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'secondary' : 'outline'} className="text-[10px] shrink-0">
                            {rec.priority}
                          </Badge>
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{rec.action}</p>
                            {rec.expected_impact && (
                              <p className="text-xs text-muted-foreground mt-0.5">{rec.expected_impact}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seasonal Factors */}
                {forecast.ai_insights.seasonal_factors?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Seasonal Factors</h4>
                    <div className="flex flex-wrap gap-2">
                      {keyed(forecast.ai_insights.seasonal_factors).map(({ item: factor, key }) => (
                        <Badge key={key} variant="outline" className="text-xs">{factor}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Critical Products Table */}
          {forecast.critical_products?.length > 0 && (
            <Card className="border-red-200 dark:border-red-900" data-testid="critical-products-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-4 w-4" /> Critical Stockout Risk
                    </CardTitle>
                    <CardDescription>
                      Products with ≤7 days of stock remaining - immediate action required
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={selectAllCritical}
                      data-testid="select-all-critical-btn"
                    >
                      Select All
                    </Button>
                    {selectedProducts.size > 0 && (
                      <Button 
                        size="sm" 
                        className="gap-1.5 bg-primary"
                        onClick={() => setPODialogOpen(true)}
                        data-testid="create-po-btn"
                      >
                        <Truck className="h-4 w-4" />
                        Create PO ({selectedProducts.size})
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-center">Current Stock</TableHead>
                        <TableHead className="text-center">Avg Daily Sales</TableHead>
                        <TableHead className="text-center">Days Until Stockout</TableHead>
                        <TableHead className="text-center">Trend</TableHead>
                        <TableHead className="text-right">30-Day Forecast</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {forecast.critical_products.map((product) => (
                        <TableRow key={product.product_id} data-testid={`critical-row-${product.product_id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selectedProducts.has(product.product_id)}
                              onCheckedChange={() => toggleProductSelection(product.product_id)}
                              data-testid={`select-${product.product_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{product.product_name}</p>
                              <p className="text-xs text-muted-foreground">{product.category}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="destructive">{product.current_stock}</Badge>
                          </TableCell>
                          <TableCell className="text-center font-mono text-sm">
                            {product.avg_daily_sales}
                          </TableCell>
                          <TableCell className="text-center">
                            <StockoutBadge days={product.days_until_stockout} />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <TrendIcon trend={product.trend} />
                              <span className="text-xs capitalize">{product.trend}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {product.forecast_demand_next_30} units
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Warning Products Table */}
          {forecast.warning_products?.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-900" data-testid="warning-products-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                      <Clock className="h-4 w-4" /> Warning Stockout Risk
                    </CardTitle>
                    <CardDescription>
                      Products with 8-14 days of stock remaining - plan reorder soon
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAllWarning}
                    data-testid="select-all-warning-btn"
                  >
                    Add All to Selection
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-center">Current Stock</TableHead>
                        <TableHead className="text-center">Avg Daily Sales</TableHead>
                        <TableHead className="text-center">Days Until Stockout</TableHead>
                        <TableHead className="text-center">Trend</TableHead>
                        <TableHead className="text-right">30-Day Forecast</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {forecast.warning_products.map((product) => (
                        <TableRow key={product.product_id} data-testid={`warning-row-${product.product_id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selectedProducts.has(product.product_id)}
                              onCheckedChange={() => toggleProductSelection(product.product_id)}
                              data-testid={`select-warning-${product.product_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{product.product_name}</p>
                              <p className="text-xs text-muted-foreground">{product.category}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{product.current_stock}</Badge>
                          </TableCell>
                          <TableCell className="text-center font-mono text-sm">
                            {product.avg_daily_sales}
                          </TableCell>
                          <TableCell className="text-center">
                            <StockoutBadge days={product.days_until_stockout} />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <TrendIcon trend={product.trend} />
                              <span className="text-xs capitalize">{product.trend}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {product.forecast_demand_next_30} units
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* All Forecasts Table */}
          <Card data-testid="all-forecasts-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> All Product Forecasts
              </CardTitle>
              <CardDescription>
                Complete demand analysis for all tracked products
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full max-h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-center">Stock</TableHead>
                      <TableHead className="text-center">Avg/Day</TableHead>
                      <TableHead className="text-center">Stockout</TableHead>
                      <TableHead className="text-center">Trend</TableHead>
                      <TableHead className="text-center">Recent 30d</TableHead>
                      <TableHead className="text-right">Forecast 30d</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forecast.all_forecasts?.map((product) => (
                      <TableRow key={product.product_id} data-testid={`forecast-row-${product.product_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm truncate max-w-[200px]">{product.product_name}</p>
                            <p className="text-xs text-muted-foreground">{product.category}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={product.current_stock <= product.min_stock_level ? "destructive" : "outline"}>
                            {product.current_stock}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">
                          {product.avg_daily_sales}
                        </TableCell>
                        <TableCell className="text-center">
                          <StockoutBadge days={product.days_until_stockout} />
                        </TableCell>
                        <TableCell className="text-center">
                          <TrendIcon trend={product.trend} />
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">
                          {product.recent_30_day_sales}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {product.forecast_demand_next_30}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Timestamp */}
          <p className="text-xs text-muted-foreground text-center">
            Forecast generated at: {new Date(forecast.generated_at).toLocaleString()}
          </p>
        </>
      )}

      {/* Empty State */}
      {!forecast && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <TrendingUp className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium">No Forecast Generated</h3>
            <p className="text-sm text-muted-foreground mt-1 text-center max-w-md">
              Configure the analysis parameters above and click "Generate Forecast" to get AI-powered demand predictions based on your sales history.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Auto PO Creation Dialog */}
      <ForecastPODialog
        open={poDialogOpen}
        onOpenChange={setPODialogOpen}
        selectedCount={selectedProducts.size}
        reorderDays={reorderDays}
        setReorderDays={setReorderDays}
        onConfirm={handleCreatePO}
        loading={creatingPO}
      />
    </div>
  );
}
